import HelpOutline from '@mui/icons-material/HelpOutline';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import PlayCircleFilledWhiteOutlinedIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import { MenuItem } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { openMoveableFrame, updateWhatsNewState } from '../../../../../actions';
import RoundButton from '../../../../../components/RoundButton';
import { useGlobalization } from '../../../../../contexts/GlobalizationProvider';
import { useGetCurrentApp, useWhatsNew } from '../../../../../hooks';
import { Applications, HeaderMenuOption } from '../../../../../types';
import { useIsFeatureEnabled } from '../../../../../utils';
import { useWeaveThemeContext } from '../../../theme/WeaveContext';
import { HelpBotIcon } from '../icons/HelpBotIcon';
import { NewIcon } from '../icons/NewIcon';
import { SupportIcon } from '../icons/SupportIcon';
import {
	BetaTag,
	MenuDivider,
	MenuLink,
	RoundButtonContainer,
	StyledLaunchIcon,
	StyledMenu,
	StyledWhatsNewLabel,
} from './HelpMenu.styles';

export const helpCenterUrls: Record<Applications, string> = {
	Plant: 'https://help.autodesk.com/view/INF360P/ENU/',
	Insight: 'https://help.autodesk.com/view/INF360I/ENU/',
	Asset: 'https://help.autodesk.com/view/INF360A/ENU/',
	Model: 'https://help.autodesk.com/view/INNCS/ENU/',
};

const helpUrls: Record<
	Applications,
	{
		getStarted: string;
		whatsNew: string;
		helpCenter: string;
	}
> = {
	Plant: {
		getStarted: '?guid=GUID-01E2215B-C607-4153-B883-AC63D22862DA',
		whatsNew: '?guid=GUID-320540AD-E958-4A0C-ABB0-9279D09641D7',
		helpCenter: '',
	},
	Insight: {
		getStarted: '?guid=GUID-BC7A99D5-4838-4A39-AAC6-8A93905FFCE5',
		whatsNew: '?guid=GUID-CCFD3F7B-4991-448F-995E-474B18251285',
		helpCenter: '',
	},
	Asset: {
		getStarted: '?guid=GUID-0BBFB98A-7A3F-4E70-92FE-0D51683A9864',
		whatsNew: '?guid=GUID-61EAE6A4-73FE-4D63-85AF-62368F9A212D',
		helpCenter: '',
	},
	Model: {
		getStarted: '?guid=GUID-E13FE243-6FFE-427E-AF57-1CF42D80B546',
		whatsNew: '?guid=GUID-FF5160AC-52DD-4A3C-90CB-77A5EAE0BF7C',
		helpCenter: '',
	},
};

const contactSupportUrl = 'https://www.autodesk.com/support/contact-support';
interface Props {
	helpUrl?: string | false;
	withHelpBot?: boolean;
	customOptions?: HeaderMenuOption[];
}

export const HelpMenu = ({
	helpUrl = 'https://help.autodesk.com/view/INF360I/ENU/',
	withHelpBot,
	customOptions,
}: Props) => {
	const history = useHistory();
	const { pathname } = useLocation();
	const dispatch = useDispatch();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const { t } = useGlobalization();
	const categorizedLinksAreEnabled = useIsFeatureEnabled(
		'info360-categorized-help-links',
	);
	const { isWeaveTheme, weaveThemeConsumer } = useWeaveThemeContext();
	const resolvedRoles = weaveThemeConsumer?.resolvedRoles;

	const currentApp: Applications = useGetCurrentApp();
	const helpCenterUrl = helpCenterUrls[currentApp];
	const entitlementUrls = helpUrls[currentApp];

	const openUrl = (url: string | false) => {
		if (typeof url === 'string') {
			window.open(url, '_blank', 'noopener,noreferrer');
		}
	};
	const { currentWhatsNewVersion, lastWhatsNewVersionSeen, isLoading } =
		useWhatsNew();

	const thereIsSomethingNew = useMemo(
		() =>
			!isLoading &&
			lastWhatsNewVersionSeen !== undefined &&
			lastWhatsNewVersionSeen !== currentWhatsNewVersion &&
			!customOptions,

		[lastWhatsNewVersionSeen, currentWhatsNewVersion, isLoading],
	);

	const textColor = isWeaveTheme
		? resolvedRoles['colorScheme.text.default']
		: 'white';

	const backgroundColor =
		isWeaveTheme && resolvedRoles['menu.backgroundColor']
			? resolvedRoles['menu.backgroundColor']
			: '#43575d';

	const closeMenu = () => setAnchorEl(null);

	const helpMenuOptions = useMemo(() => {
		if (customOptions?.length) {
			return customOptions;
		}
		return [
			{
				title: t('Get started'),
				content: (
					<>
						<PlayCircleFilledWhiteOutlinedIcon />
						<span>{t('Get started')}</span>
					</>
				),
				onClick: () =>
					openUrl(`${helpCenterUrl}${entitlementUrls.getStarted}`),
			},
			{
				title: t('What´s new'),
				content: (
					<>
						<NewIcon />
						<StyledWhatsNewLabel withRedDot={thereIsSomethingNew}>
							{t('What´s new')}
						</StyledWhatsNewLabel>
					</>
				),
				onClick: () => {
					dispatch(
						updateWhatsNewState({
							routeToReturnOnClose: pathname,
						}),
					);
					history.push('/whats-new');
					closeMenu();
				},
			},
			...(withHelpBot
				? [
						{
							title: t('AI Help Bot'),
							content: (
								<>
									<HelpBotIcon />
									<span>{t('AI Help Bot')}</span>
									<BetaTag>BETA</BetaTag>
								</>
							),
							onClick: () => {
								dispatch(openMoveableFrame());
								closeMenu();
							},
						},
				  ]
				: []),
			{ content: <MenuDivider /> },
			{
				title: t('Help center'),
				content: (
					<>
						<HelpOutlineOutlinedIcon />
						<span>{t('Help center')}</span>
						<StyledLaunchIcon />
					</>
				),
				onClick: () =>
					openUrl(`${helpCenterUrl}${entitlementUrls.helpCenter}`),
			},
			{
				title: t('Contact support'),
				content: (
					<>
						<SupportIcon />
						<span>{t('Contact support')}</span>
						<StyledLaunchIcon />
					</>
				),
				onClick: () => openUrl(contactSupportUrl),
			},
		];
	}, [customOptions, thereIsSomethingNew]);

	return (
		<>
			<RoundButtonContainer withRedDot={thereIsSomethingNew}>
				<RoundButton
					onClick={event =>
						categorizedLinksAreEnabled
							? setAnchorEl(event.currentTarget)
							: openUrl(helpUrl)
					}
					data-cy="app-help"
					title={t('Help', {
						context: 'Header help button tooltip',
					})}>
					<HelpOutline htmlColor={textColor} />
				</RoundButton>
			</RoundButtonContainer>
			<StyledMenu
				MenuListProps={{
					style: {
						borderRadius: 0,
						margin: 0,
						backgroundColor,
						color: textColor,
					},
				}}
				PaperProps={{ style: { borderRadius: 0, marginTop: 5 } }}
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				onClose={() => closeMenu()}
				open={!!anchorEl}>
				{helpMenuOptions.map((option, index) => {
					if (option.title) {
						return (
							<MenuItem
								key={`${index}-${option.title}`}
								title={option?.title}
								onClick={() => {
									option.onClick?.();
									closeMenu();
								}}>
								<MenuLink color="inherit" underline="none">
									{option.content}
								</MenuLink>
							</MenuItem>
						);
					}
					return <div key={index}>{option.content}</div>;
				})}
			</StyledMenu>
		</>
	);
};
